import { useGetLatestActivePrivacyPolicy, useAcceptPrivacyPolicy } from '@exam-room/graphql';
import * as Sentry from '@sentry/react';
import { useDisclosure } from '@shared/hooks';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { Button, Card, Form } from '../../../components';
import { useAuth, useExamEvent } from '../../../providers';
import { PATHS } from '../../constants';
import { VideoModal } from '../instruction/components';

interface AcceptPrivacyNoticeInput {
  accept: boolean;
}

function PrivacyNotice() {
  const [showVideo, { close }] = useDisclosure(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const { control, watch, setValue } = useForm<AcceptPrivacyNoticeInput>({
    defaultValues: {
      accept: false,
    },
  });
  const { accept } = watch();
  const { examCardId } = useParams();

  const { language } = i18n;

  const { examEvent } = useExamEvent();

  const { data: privacyPolicyData, loading: isPolicyLoading } = useGetLatestActivePrivacyPolicy();

  const [acceptPrivacyPolicy, { loading: isAcceptingPrivacyPolicy }] = useAcceptPrivacyPolicy();

  const policy = privacyPolicyData?.cluGetLatestActivePrivacyPolicy?.privacyPolicy;
  const documentUrl = policy?.documentUrl;

  const handleOnAccept = () => {
    acceptPrivacyPolicy({
      variables: {
        acceptPrivacyPolicyInput: {
          policyId: policy?.id || '',
          isAccepted: true,
        },
      },
      onCompleted: () => {
        const termsAndConditionsPath = generatePath(PATHS.Exam.TermsAndConditions, { examCardId });
        navigate(termsAndConditionsPath);
      },
      onError: (error) => {
        console.error('Error accepting privacy policy:', error);
        Sentry.captureException(error, {
          tags: {
            feature: 'accept-privacy-policy',
            policyId: policy?.id || '',
          },
        });
      },
    });
  };

  const handleOnReject = async () => {
    await logout();
    navigate(PATHS.Login);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
    const isBottom = scrollHeight - scrollTop - clientHeight < 24;
    if (isBottom && !isScrolledToBottom) {
      setIsScrolledToBottom(true);
    }
  };

  const handleOnCheckboxChange = () => {
    setValue('accept', !accept);
  };

  useEffect(() => {
    const fetchContent = async () => {
      if (!documentUrl) {
        setContent('No privacy policy available.');
        return;
      }
      let localizedUrl = documentUrl;
      if (documentUrl?.includes('{locale}')) {
        localizedUrl = documentUrl.replace('{locale}', language);
      }
      try {
        const { data } = await axios.get(localizedUrl);
        setContent(data);
      } catch (error) {
        console.error('Error fetching privacy policy content:', error);
        setContent('No privacy policy available.');
        Sentry.captureException(error, {
          tags: {
            feature: 'fetch-privacy-policy',
            url: localizedUrl,
          },
        });
      }
    };
    fetchContent();
  }, [documentUrl, language]);

  const { metadata } = examEvent;

  const { videoURL } = metadata;

  return (
    <>
      <VideoModal show={showVideo} onClose={close} videoUrl={videoURL} />
      <Card className='mx-auto w-[1200px] gap-6'>
        <div
          className='border-border-divider bg-grey-50 flex max-h-[calc(100vh-300px)] flex-col gap-2 overflow-y-scroll rounded-md border-1 p-6'
          onScroll={handleScroll}
        >
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children, ...props }) => (
                <h1 className='text-center text-3xl font-bold' {...props}>
                  {children}
                </h1>
              ),
              h2: ({ children, ...props }) => (
                <h2 className='w-full text-xl font-bold' {...props}>
                  {children}
                </h2>
              ),
              h3: ({ children, ...props }) => (
                <h3 className='w-full text-lg font-bold' {...props}>
                  {children}
                </h3>
              ),
              h4: ({ children, ...props }) => (
                <h4 className='text-md w-full font-bold' {...props}>
                  {children}
                </h4>
              ),
              p: ({ ...props }) => <p className='text-body2' {...props} />,
              ul: ({ ...props }) => <ul className='list-disc pl-6' {...props} />,
              li: ({ ...props }) => <li className='text-body' {...props} />,
            }}
          >
            {content}
          </Markdown>
        </div>
        <Form.Checkbox
          name='accept'
          control={control}
          required
          checked={accept}
          onChange={handleOnCheckboxChange}
          disabled={!isScrolledToBottom}
        >
          {t('privacyNotice.acceptCheckbox')}
        </Form.Checkbox>
        <div className='flex flex-row justify-center gap-4'>
          <Button onClick={handleOnReject} size='lg' variant='outlined' className='w-[120px]'>
            {t('decline')}
          </Button>
          <Button
            onClick={handleOnAccept}
            size='lg'
            className='w-[120px]'
            disabled={!accept || isAcceptingPrivacyPolicy || isPolicyLoading}
          >
            {t('accept')}
          </Button>
        </div>
      </Card>
    </>
  );
}

const PrivacyNoticeRoute = {
  path: PATHS.Exam.PrivacyNotice,
  element: <PrivacyNotice />,
};

export default PrivacyNoticeRoute;
